/**
 * « Ce message peut-il s'afficher devant un client ? » — la règle, en un seul
 * endroit.
 *
 * Elle vit ici, dans le paquet que le serveur ET les deux fronts consomment déjà,
 * parce qu'elle est lue des deux côtés de la frontière HTTP :
 *
 * - `packages/queries/src/lib/api-errors.ts` s'en sert à l'arrivée, pour ne
 *   jamais afficher au visiteur du site public un texte écrit pour un
 *   développeur ;
 * - le spec d'architecture des catalogues d'erreurs du serveur s'en sert au
 *   départ, pour relire chaque `message` de catalogue avec exactement les règles
 *   qui le filtreront en production.
 *
 * Deux copies des expressions régulières divergeraient au premier ajout, et le
 * garde-fou côté serveur mentirait — il validerait des messages que le front
 * remplacerait quand même par son repli générique.
 */
/**
 * Messages génériques que NestJS renvoie sur un 500 non maîtrisé. Ils ne doivent
 * jamais s'afficher : le serveur les remplace désormais (ClientFacingExceptionFilter),
 * on garde ce garde-fou côté client pour les versions d'API plus anciennes.
 */
const GENERIC_SERVER_MESSAGES = new Set([
    'bad gateway',
    'gateway timeout',
    'internal server error',
    'service unavailable',
]);
/** Au-delà, ce n'est plus une phrase mais un dépôt de trace. */
const MAX_CLIENT_MESSAGE_LENGTH = 300;
/**
 * Un message rédigé par notre serveur est une phrase française qui dit quoi faire.
 * Tout ce qui porte l'une de ces marques vient d'ailleurs — erreur OpenAI ou
 * WordPress recopiée telle quelle, trace d'exception, réponse d'un hébergeur — et
 * ne dit rien à quelqu'un qui ne code pas.
 */
const TECHNICAL_MESSAGE_PATTERNS = [
    // Mots anglais des erreurs de prestataires. Aucun n'existe en français, et les
    // faux amis n'en sont pas : « erreur », « réponse », « invalide », « se connecter »
    // ne matchent pas. « plan », « port », « prompt » restent dehors : eux sont français.
    /\b(the|is|are|was|were|not|invalid|failed|failure|error|request|response|status|unexpected|missing|required|undefined|cannot|can't|unable|timeout|timed|exceeded|internal|gateway|server|token|key|must|should|this|that|with|from|your|please|try|again|rate|limit|reached|retry|connect|connection|network|socket|host|refused|reset|parse|fetch|null)\b/i,
    // Variable d'environnement ou constante technique (OPENAI_API_KEY, DATABASE_URL)
    /\b[A-Z][A-Z0-9]*(?:_[A-Z0-9]+)+\b/,
    // Code errno de Node recopié tel quel. Énuméré plutôt que deviné : une
    // référence de dossier commençant par un E n'est pas une panne réseau.
    /\bE(?:CONN|TIMEDOUT|NOTFOUND|NETUNREACH|HOSTUNREACH|PIPE|ACCES|NOENT|AI_AGAIN)[A-Z]*\b/,
    // Code HTTP. Nommé ou en tête de segment, jamais nu : un montant à trois
    // chiffres (« 450 € ») n'est pas un code d'erreur.
    /\bHTTP\b|\b(?:erreur|error|code|statut|status)\s*:?\s*[45]\d{2}\b|(?:^|[·:|–—]\s*)[45]\d{2}\b/i,
    // Corps JSON ou objet recopié dans le message
    /[{}]/,
    // Commande recopiée depuis un journal
    /\b(npm|pnpm|yarn|docker|sudo|curl)\b/i,
];
/** Vrai quand un message peut s'afficher devant un client du site public. */
export const isClientReadable = (message) => message.length <= MAX_CLIENT_MESSAGE_LENGTH &&
    !GENERIC_SERVER_MESSAGES.has(message.toLowerCase()) &&
    !TECHNICAL_MESSAGE_PATTERNS.some((pattern) => pattern.test(message));
